import { useState } from 'react'
import { EyeOff, Eye, Shield, User } from 'lucide-react'

const AnonymityToggle = ({ isAnonymous, onToggle, size = 'md' }) => {
  const [isHovered, setIsHovered] = useState(false)

  const sizeClasses = {
    sm: {
      container: 'h-5 w-9',
      dot: 'h-3 w-3',
      icon: 'w-2.5 h-2.5',
      text: 'text-xs'
    },
    md: {
      container: 'h-6 w-11',
      dot: 'h-4 w-4',
      icon: 'w-3 h-3',
      text: 'text-sm'
    },
    lg: {
      container: 'h-7 w-12',
      dot: 'h-5 w-5',
      icon: 'w-3.5 h-3.5',
      text: 'text-base'
    }
  }

  const classes = sizeClasses[size]

  return (
    <div 
      className="flex items-center space-x-2"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <button
        type="button"
        onClick={() => onToggle(!isAnonymous)}
        className={`
          ${classes.container}
          relative inline-flex flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
          transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-anonymous-500 focus:ring-offset-2
          ${isAnonymous ? 'bg-anonymous-500' : 'bg-gray-200'}
        `}
        aria-pressed={isAnonymous}
        aria-label={isAnonymous ? 'Post anonymously' : 'Post with your name'}
      >
        <span className="sr-only">
          {isAnonymous ? 'Post anonymously' : 'Post with your name'}
        </span>
        <span
          className={`
            ${classes.dot}
            pointer-events-none inline-block rounded-full bg-white shadow ring-0
            transition duration-200 ease-in-out
            ${isAnonymous ? 'translate-x-5' : 'translate-x-0'}
            flex items-center justify-center
          `}
        >
          {isAnonymous ? (
            <EyeOff className={`${classes.icon} text-anonymous-600`} />
          ) : (
            <Eye className={`${classes.icon} text-gray-500`} />
          )}
        </span>
      </button>
      
      <div className="flex flex-col">
        <span className={`${classes.text} font-medium ${isAnonymous ? 'text-anonymous-600' : 'text-gray-600'}`}>
          {isAnonymous ? 'Anonymous' : 'Identified'}
        </span>
        {isHovered && (
          <span className="text-xs text-gray-400">
            {isAnonymous 
              ? 'Your identity will be hidden' 
              : 'Your display name will be shown'}
          </span>
        )}
      </div>

      {/* Visual indicator */}
      <div className={`
        ml-2 flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium
        ${isAnonymous 
          ? 'bg-anonymous-100 text-anonymous-700' 
          : 'bg-blue-100 text-blue-700'}
      `}>
        {isAnonymous ? (
          <>
            <Shield className="w-3 h-3" />
            <span>Protected</span>
          </>
        ) : (
          <>
            <User className="w-3 h-3" />
            <span>Public</span>
          </>
        )}
      </div>
    </div>
  )
}

export default AnonymityToggle